import React, { useState, useMemo } from 'react';
import { Box, Button, Card, CardActionArea, Typography } from '@mui/material';
import { ArrowForwardRounded as ArrowIcon, CompareArrowsRounded as ArbIcon, EventBusyRounded as EmptyIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useApi, useLiveScores } from '../hooks';
import { tokens } from '../theme';
import { PageHeader, StatTile, SectionTitle, CardGridSkeleton, EmptyState, ErrorState, cardGridSx, LiveDot } from './ui';
import { MatchCard, PredictionDrawer } from './MatchCard';

const Dashboard = () => {
  const navigate = useNavigate();
  const predictions = useApi('/api/predictions');
  const stats = useApi('/api/stats', { refreshMs: 60000 });
  const arbitrage = useApi('/api/arbitrage');
  const { scoreFor } = useLiveScores();
  const [selected, setSelected] = useState(null);

  const sorted = useMemo(
    () => [...(predictions.data || [])].sort((a, b) => b.confidence - a.confidence),
    [predictions.data]
  );
  const liveNow = sorted.filter(p => scoreFor(p)?.is_live);
  const topPicks = sorted.slice(0, 6);
  const favourites = sorted.filter(p => p.confidence >= 0.6).length;
  const s = stats.data;
  const arbs = arbitrage.data;

  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Box>
      <PageHeader
        eyebrow={today}
        title="Today's board"
        subtitle="Market-implied probabilities for today's matches, live scores and arbitrage opportunities in one place."
      />

      <Box display="grid" gap={{ xs: 1.5, sm: 2 }} gridTemplateColumns={{ xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }}>
        <StatTile label="Matches today" value={predictions.data ? predictions.data.length : '–'} hint={`${favourites} strong favourites`} />
        <StatTile
          label="Accuracy"
          value={s && s.completed_predictions > 0 ? `${Math.round(s.accuracy)}%` : '–'}
          hint={s ? `${s.correct_predictions} of ${s.completed_predictions} settled` : ' '}
        />
        <StatTile label="Live now" value={liveNow.length} tone={liveNow.length ? tokens.status.live : undefined} hint="refreshes every 30s" />
        <StatTile
          label="Arbitrage"
          value={arbs?.configured ? arbs.opportunities.length : '–'}
          tone={arbs?.opportunities?.length ? tokens.status.good : undefined}
          hint={arbs?.configured ? 'guaranteed-profit bets' : 'needs odds API key'}
        />
      </Box>

      {liveNow.length > 0 && (
        <>
          <SectionTitle>
            <Box display="inline-flex" alignItems="center" gap={1.25}><LiveDot /> Live now</Box>
          </SectionTitle>
          <Box sx={cardGridSx}>
            {liveNow.map(p => <MatchCard key={p.id} prediction={p} live={scoreFor(p)} onOpen={setSelected} />)}
          </Box>
        </>
      )}

      <SectionTitle action={<Button endIcon={<ArrowIcon />} onClick={() => navigate('/predictions')}>All predictions</Button>}>
        Top picks
      </SectionTitle>
      {predictions.loading ? (
        <CardGridSkeleton count={3} />
      ) : predictions.error ? (
        <ErrorState message={predictions.error} onRetry={predictions.reload} />
      ) : topPicks.length === 0 ? (
        <EmptyState icon={<EmptyIcon />} title="No predictions yet today" body="Predictions are generated every morning at 9:00 UTC for matches with bookmaker odds." />
      ) : (
        <Box sx={cardGridSx}>
          {topPicks.map(p => <MatchCard key={p.id} prediction={p} live={scoreFor(p)} onOpen={setSelected} />)}
        </Box>
      )}

      <Card sx={{
        mt: 4,
        background: `linear-gradient(120deg, ${alpha(tokens.accent, 0.16)}, ${alpha(tokens.outcome.away, 0.08)})`,
        borderColor: alpha(tokens.accent, 0.3),
      }}>
        <CardActionArea onClick={() => navigate('/arbitrage')} sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-start' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: alpha(tokens.accent, 0.2), color: 'primary.main', flexShrink: 0 }}>
            <ArbIcon />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6">
              {arbs?.configured && arbs.opportunities.length > 0
                ? `${arbs.opportunities.length} arbitrage ${arbs.opportunities.length === 1 ? 'opportunity' : 'opportunities'} open`
                : 'Arbitrage scanner'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Back every outcome at a different bookmaker and lock in a profit whatever the result.
            </Typography>
          </Box>
          <ArrowIcon sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }} />
        </CardActionArea>
      </Card>

      <PredictionDrawer prediction={selected} live={scoreFor(selected)} onClose={() => setSelected(null)} />
    </Box>
  );
};

export default Dashboard;
